import { bopApi } from '@/lib/api/client';
import { BROKER_LIST_LIMIT, type PaginatedResponse } from './brokers';

// Weekly broker fee settlements — written by fast-broker-api's Monday BROKER_FEES
// report into the shared fast table, reviewed here by Ops. Approving (PAY) initiates
// a real payout via fast-funds/Kinexys; do not confuse with the STATIC `fee_split`
// config on the broker record.

const FEE_SETTLEMENTS_STATE_PATH = 'v1/brokers/fee-settlements/state';

export const FEE_SETTLEMENT_STATUSES = ['PENDING_REVIEW', 'REJECTED', 'PAYMENT_INITIATED', 'PAYMENT_PENDING', 'PAID', 'FAILED'] as const;
export type FeeSettlementStatus = (typeof FEE_SETTLEMENT_STATUSES)[number];

/**
 * All monetary fields are DECIMAL STRINGS at up to 8 dp — render verbatim /
 * format as strings, never `Number()` them. `payable_amount` is server-derived:
 * `broker_payment` truncated to whole cents (what Kinexys actually pays).
 */
export interface WireFeeSettlement {
  settlement_id: string;
  broker_account_id: string;
  /** lowercase broker record name — fast-funds resolves the Kinexys account from it */
  broker_name: string;
  /** ISO-8601, inclusive (previous Monday 00:00Z) */
  period_from: string;
  /** ISO-8601, exclusive (following Monday 00:00Z) */
  period_to: string;
  /** always 'USD' today */
  currency: string;
  total_trade_fees: string;
  total_liquidation_fees: string;
  total_fees: string;
  /** broker's exact share at 8 dp — NOT the paid amount */
  broker_payment: string;
  onetrading_share: string;
  /** null when broker_payment is malformed */
  payable_amount: string | null;
  trade_count: number;
  liquidation_count: number;
  /** Kinexys ISO-20022 EndToEndId */
  payment_reference: string;
  status: FeeSettlementStatus;
  /** ISO-8601 */
  created_at: string;
  /** ISO-8601 */
  updated_at: string;
  rejection_reason?: string;
  failure_reason?: string;
  /** ISO-8601, present once Kinexys confirms */
  paid_at?: string;
}

export interface FeeSettlementCapabilities {
  canView: boolean;
  canReview: boolean;
}

export interface FeeSettlementsResponse extends PaginatedResponse<WireFeeSettlement> {
  capabilities: FeeSettlementCapabilities;
}

export const getBrokerFeeSettlements = (brokerId: string, cursor?: string): Promise<FeeSettlementsResponse> =>
  bopApi
    .get(`v1/brokers/${brokerId}/fee-settlements`, {
      searchParams: { limit: BROKER_LIST_LIMIT, ...(cursor ? { cursor } : {}) },
    })
    .json<FeeSettlementsResponse>();

export type FeeSettlementAction = 'PAY' | 'REJECT';

export interface UpdateFeeSettlementStateRequest {
  broker_account_id: string;
  settlement_id: string;
  action: FeeSettlementAction;
  /** required when action is REJECT, ≤1000 chars */
  rejection_reason?: string;
  /** operator justification — written to the bop-api audit log */
  comment: string;
}

// Success body is empty — never .json() this.
export const updateFeeSettlementState = (request: UpdateFeeSettlementStateRequest): Promise<string> => bopApi.post(FEE_SETTLEMENTS_STATE_PATH, { json: request }).text();

/**
 * Dynamo returns the partition in sort-key order, and the sort key is a random
 * UUID — order the loaded rows newest period first for display.
 */
export const sortFeeSettlementsByPeriod = (settlements: WireFeeSettlement[]): WireFeeSettlement[] =>
  [...settlements].sort((a, b) => Date.parse(b.period_from) - Date.parse(a.period_from) || Date.parse(b.created_at) - Date.parse(a.created_at));
